<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <h1 class="page-title">Kategorien verwalten</h1>
        <p class="page-subtitle">Erstellen und bearbeiten Sie Ihre Kategorien</p>
      </div>
      <button v-if="$can('categories.create')" class="primary-btn" @click="openCreateDialog">
        <v-icon size="16">mdi-plus</v-icon>
        <span>Neue Kategorie</span>
      </button>
    </div>

    <!-- Toolbar with view toggle -->
    <div class="toolbar mb-6">
      <div class="result-count">
        <template v-if="!loading">
          {{ categories.length }}
          {{ categories.length === 1 ? 'Kategorie' : 'Kategorien' }}
        </template>
      </div>

      <div class="view-toggle">
        <button
          class="view-btn"
          :class="{ active: viewMode === 'grid' }"
          title="Grid-Ansicht"
          @click="setViewMode('grid')"
        >
          <v-icon size="16">mdi-view-grid-outline</v-icon>
        </button>
        <button
          class="view-btn"
          :class="{ active: viewMode === 'list' }"
          title="Listen-Ansicht"
          @click="setViewMode('list')"
        >
          <v-icon size="16">mdi-format-list-bulleted</v-icon>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="skel-card" v-for="i in 4" :key="`skel-${i}`" :style="viewMode === 'list' ? 'height: 60px' : 'height: 120px'"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="categories.length === 0" class="empty-state">
      <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-tag-off-outline</v-icon>
      <h3 class="empty-title">Keine Kategorien vorhanden</h3>
      <p class="empty-desc">Erstellen Sie Ihre erste Kategorie, um loszulegen.</p>
      <button v-if="$can('categories.create')" class="primary-btn mt-4" @click="openCreateDialog">
        <v-icon size="16">mdi-plus</v-icon>
        Erste Kategorie erstellen
      </button>
    </div>

    <!-- Grid View -->
    <div v-else-if="viewMode === 'grid'" class="grid-cats">
      <div
        v-for="(category, idx) in categories"
        :key="category.id"
        class="cat-card"
      >
        <div class="cat-icon" :style="{ background: iconBg(idx), color: iconFg(idx) }">
          <span>{{ getIconInitials(category.name) }}</span>
        </div>

        <div class="cat-body">
          <div class="cat-name">{{ category.name }}</div>
          <div class="cat-icon-name">
            <v-icon size="12" color="#aaa" class="mr-1">mdi-shape-outline</v-icon>
            {{ category.icon || 'Kein Icon' }}
          </div>
        </div>

        <div class="cat-actions">
          <button v-if="$can('categories.edit')" class="icon-btn" title="Bearbeiten" @click="openEditDialog(category)">
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </button>
          <button v-if="$can('categories.delete')" class="icon-btn icon-btn-danger" title="Löschen" @click="deleteCategory(category)">
            <v-icon size="16">mdi-delete-outline</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="list-wrap">
      <div class="list-header">
        <div class="list-col-icon"></div>
        <div class="list-col-name">Name</div>
        <div class="list-col-icon-name">Icon</div>
        <div class="list-col-actions"></div>
      </div>
      <div
        v-for="(category, idx) in categories"
        :key="category.id"
        class="list-row"
      >
        <div class="list-col-icon">
          <div class="cat-icon-sm" :style="{ background: iconBg(idx), color: iconFg(idx) }">
            {{ getIconInitials(category.name) }}
          </div>
        </div>
        <div class="list-col-name">{{ category.name }}</div>
        <div class="list-col-icon-name">{{ category.icon || '—' }}</div>
        <div class="list-col-actions">
          <button v-if="$can('categories.edit')" class="icon-btn" title="Bearbeiten" @click="openEditDialog(category)">
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </button>
          <button v-if="$can('categories.delete')" class="icon-btn icon-btn-danger" title="Löschen" @click="deleteCategory(category)">
            <v-icon size="16">mdi-delete-outline</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Backdrop + Dialog -->
    <div v-if="dialog" class="dialog-backdrop" @click="!saving && closeDialog()"></div>
    <v-dialog v-model="dialog" max-width="520" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button
          class="close-btn"
          :disabled="saving"
          @click="closeDialog"
          aria-label="Schließen"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>

        <div class="dh">
          <div class="dh-icon">
            <v-icon size="22" color="#0a1628">
              {{ editMode ? 'mdi-tag-outline' : 'mdi-tag-plus-outline' }}
            </v-icon>
          </div>
          <div>
            <div class="dh-title">{{ editMode ? 'Kategorie bearbeiten' : 'Neue Kategorie' }}</div>
            <div class="dh-sub">
              {{ editMode ? 'Namen oder Icon anpassen' : 'Gruppiere deine Sections sinnvoll' }}
            </div>
          </div>
        </div>

        <div class="db">
          <div class="fg mb-4">
            <label class="fl">Name *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'name', 'iw-err': errors.name }">
              <v-icon size="15" color="rgba(0,0,0,0.25)">mdi-tag-outline</v-icon>
              <input
                v-model="formData.name"
                class="ii"
                placeholder="z.B. Hero, Footer, Product"
                :disabled="saving"
                @focus="focusField = 'name'"
                @blur="focusField = null"
              />
            </div>
            <div v-if="errors.name" class="err-text">{{ errors.name }}</div>
          </div>

          <div class="fg">
            <label class="fl">Icon (Shopify Polaris)</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'icon' }">
              <v-icon size="15" color="rgba(0,0,0,0.25)">mdi-shape-outline</v-icon>
              <input
                v-model="formData.icon"
                class="ii"
                placeholder="z.B. HomeIcon, ProductIcon"
                :disabled="saving"
                @focus="focusField = 'icon'"
                @blur="focusField = null"
              />
            </div>
            <div class="hint">
              Polaris Icon-Name.
              <a href="https://polaris-react.shopify.com/icons" target="_blank">Bibliothek öffnen</a>
            </div>
          </div>

          <div v-if="errorMessage" class="alert-error mt-4">
            <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ errorMessage }}
          </div>
        </div>

        <div class="dff">
          <button class="bo" :disabled="saving" @click="closeDialog">Abbrechen</button>
          <button class="bp" :disabled="saving" @click="saveCategory">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            <span v-else>{{ editMode ? 'Speichern' : 'Erstellen' }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor === 'success' ? '#2E7D32' : '#D32F2F'" timeout="3000" location="top">
      <div class="d-flex align-center" style="gap: 8px">
        <v-icon size="18" color="white">{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        {{ snackbarMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

const PALETTE = [
  { bg: 'rgba(91,91,214,0.1)',  fg: '#5B5BD6' },
  { bg: 'rgba(46,125,50,0.1)',  fg: '#2E7D32' },
  { bg: 'rgba(255,152,0,0.1)',  fg: '#E65100' },
  { bg: 'rgba(21,101,192,0.1)', fg: '#1565C0' },
  { bg: 'rgba(124,77,255,0.1)', fg: '#7C4DFF' },
  { bg: 'rgba(211,47,47,0.1)',  fg: '#D32F2F' },
  { bg: 'rgba(0,131,143,0.1)',  fg: '#00838F' },
];

export default {
  name: 'CategoryOverview',

  data: () => ({
    categories: [],
    loading: false,
    dialog: false,
    editMode: false,
    saving: false,
    formData: { id: null, name: '', icon: '' },
    errors: { name: '' },
    errorMessage: '',
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    viewMode: localStorage.getItem('categories.viewMode') === 'list' ? 'list' : 'grid',
    focusField: null,
  }),

  mounted() { this.loadCategories(); },

  methods: {
    iconBg(i) { return PALETTE[i % PALETTE.length].bg; },
    iconFg(i) { return PALETTE[i % PALETTE.length].fg; },

    getIconInitials(name) {
      if (!name) return '?';
      const words = name.trim().split(/\s+/);
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem('categories.viewMode', mode);
    },

    async loadCategories() {
      this.loading = true;
      try {
        const result = await axios.get('/api/category/get');
        this.categories = result.data;
      } catch (e) {
        console.error('Fehler beim Laden der Kategorien!', e);
        this.showSnackbar('Fehler beim Laden der Kategorien', 'error');
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.editMode = false;
      this.formData = { id: null, name: '', icon: '' };
      this.errors = { name: '' };
      this.errorMessage = '';
      this.dialog = true;
    },

    openEditDialog(category) {
      this.editMode = true;
      this.formData = { id: category.id, name: category.name, icon: category.icon || '' };
      this.errors = { name: '' };
      this.errorMessage = '';
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
    },

    validateForm() {
      this.errors = { name: '' };
      if (!this.formData.name || !this.formData.name.trim()) {
        this.errors.name = 'Name ist erforderlich';
        return false;
      }
      return true;
    },

    async saveCategory() {
      if (!this.validateForm()) return;

      this.saving = true;
      this.errorMessage = '';

      try {
        if (this.editMode) {
          await axios.post('/api/category/update', {
            id: this.formData.id,
            name: this.formData.name.trim(),
            icon: this.formData.icon.trim() || null,
          });
          this.showSnackbar('Kategorie erfolgreich aktualisiert');
        } else {
          await axios.post('/api/category/create', {
            name: this.formData.name.trim(),
            icon: this.formData.icon.trim() || null,
          });
          this.showSnackbar('Kategorie erfolgreich erstellt');
        }
        this.closeDialog();
        await this.loadCategories();
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        this.errorMessage = error.response?.data?.message || 'Fehler beim Speichern der Kategorie';
      } finally {
        this.saving = false;
      }
    },

    async deleteCategory(category) {
      if (!confirm(
        `Möchten Sie die Kategorie "${category.name}" wirklich löschen?\n\nAchtung: Dies könnte Auswirkungen auf zugeordnete Sections haben.`
      )) return;

      try {
        await axios.post('/api/category/delete', { id: category.id });
        this.showSnackbar('Kategorie erfolgreich gelöscht');
        await this.loadCategories();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        const message = error.response?.data?.message || 'Fehler beim Löschen der Kategorie';
        this.showSnackbar(message, 'error');
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Buttons */
.primary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s;
}
.primary-btn:hover:not(:disabled) { background: #1d3457; }
.primary-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s;
}
.secondary-btn:hover:not(:disabled) { border-color: #0a1628; }
.secondary-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.icon-btn {
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; border-radius: 8px; cursor: pointer;
  color: #666; transition: all 0.15s;
}
.icon-btn:hover { background: #f0f2f5; color: #0a1628; }
.icon-btn-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }

/* Toolbar */
.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.result-count { font-size: 12px; color: #888; font-weight: 500; }

.view-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px; background: white; border: 1px solid #eceef2; border-radius: 10px; height: 40px;
}
.view-btn {
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 7px; cursor: pointer; color: #888; transition: all 0.15s;
}
.view-btn:hover:not(.active) { background: #f8f9fb; color: #0a1628; }
.view-btn.active { background: #0a1628; color: white; }
.view-btn.active :deep(.v-icon) { color: white !important; }

/* Grid View */
.grid-cats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}
.cat-card {
  display: flex; align-items: center; gap: 16px; padding: 16px 20px;
  background: white; border: 1px solid #eceef2; border-radius: 14px;
  transition: border-color 0.15s;
}
.cat-card:hover { border-color: #0a1628; }
.cat-icon {
  width: 52px; height: 52px; min-width: 52px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 800; letter-spacing: 0.5px;
}
.cat-body { flex: 1; min-width: 0; }
.cat-name {
  font-size: 15px; font-weight: 700; color: #0a1628;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  margin-bottom: 3px;
}
.cat-icon-name {
  font-size: 12px; color: #888; display: inline-flex; align-items: center;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cat-actions { display: flex; gap: 4px; }

/* List View */
.list-wrap {
  background: white; border: 1px solid #eceef2; border-radius: 14px; overflow: hidden;
}
.list-header {
  display: grid;
  grid-template-columns: 60px minmax(0, 2fr) minmax(0, 2fr) 90px;
  align-items: center; gap: 16px; padding: 12px 20px;
  background: #fafbfc; border-bottom: 1px solid #eceef2;
  font-size: 11px; font-weight: 700; color: #999;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.list-row {
  display: grid;
  grid-template-columns: 60px minmax(0, 2fr) minmax(0, 2fr) 90px;
  align-items: center; gap: 16px; padding: 12px 20px;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s;
}
.list-row:last-child { border-bottom: none; }
.list-row:hover { background: #fafbfc; }

.cat-icon-sm {
  width: 36px; height: 36px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800;
}
.list-col-name { font-size: 14px; font-weight: 600; color: #0a1628; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.list-col-icon-name { font-size: 13px; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.list-col-actions { display: flex; gap: 4px; justify-content: flex-end; }

/* Empty state */
.empty-state {
  background: white; border: 1px solid #eceef2; border-radius: 16px;
  padding: 64px 32px; text-align: center;
}
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; }

/* Loading */
.loading-state { display: flex; flex-direction: column; gap: 12px; }
.skel-card {
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 14px;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

/* Dialog (floowseo pattern) */
.dialog-backdrop {
  position: fixed; inset: 0; background: rgba(10,22,40,0.5);
  z-index: 2000; animation: fadeBg 0.2s ease;
}
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }

.modern-dialog {
  background: white !important;
  box-shadow: 0 24px 64px rgba(10,22,40,0.18) !important;
  z-index: 2001 !important;
  position: relative;
  animation: slideUp 0.3s cubic-bezier(0.16,1,0.3,1);
  overflow: visible !important;
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.close-btn {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.dh { display: flex; align-items: center; gap: 14px; padding: 28px 28px 0; }
.dh-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10,22,40,0.06); flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 700; color: #0a1628; }
.dh-sub { font-size: 12px; color: #999; margin-top: 2px; }

.db { padding: 24px 28px; }
.dff {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 4px 28px 24px;
}

.fg { display: flex; flex-direction: column; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }
.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f {
  border-color: #0a1628; background: white;
  box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}
.iw.iw-err { border-color: #D32F2F; }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii:disabled { opacity: 0.6; }
.err-text { font-size: 11px; color: #D32F2F; margin-top: 5px; font-weight: 500; }
.hint { font-size: 11px; color: #888; margin-top: 6px; }
.hint a { color: #5B5BD6; text-decoration: none; }
.hint a:hover { text-decoration: underline; }

.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.5; cursor: not-allowed; }
.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 110px;
  padding: 10px 20px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; justify-content: center;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }

.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

@media (max-width: 768px) {
  .list-header, .list-row { grid-template-columns: 48px minmax(0, 1fr) 80px; }
  .list-col-icon-name { display: none; }
}
</style>
